<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { Menu } from '~/types/models/menu';
import { resolveMenuBySlug } from '~/utils/menu';
import ExternalLinkWarning from '~/components/ui/ExternalLinkWarning.vue';
import { filterMenuItems } from '~/utils/helpers';
import wmtLogo from '@/img/wmt.svg';
import WebsiteLogoImage from '@/img/logo.png';
import VLazyImage from 'v-lazy-image';
import { Link } from '@inertiajs/vue3';
import Container from '~/components/Container.vue';
import { useSettings } from '~/composables/useSettings';

const { websiteLogo } = useSettings();

const footerMenu = ref<Menu.Model | null>(null);

const filteredMenuItems = computed(
  () =>
    footerMenu.value?.menuItems &&
    filterMenuItems(footerMenu.value.menuItems, [
      'TextMenuItem',
      'SocialIconCollectionMenuItem',
      'StaticUrlMenuItem',
      'TitleMenuItem',
    ])
);

onMounted(() => {
  footerMenu.value = resolveMenuBySlug('footer');
});
</script>

<template>
  <footer class="footer">
    <Container class="footer__container">
      <div class="footer__top">
        <div class="footer__copyright-box">
          <Link href="/" class="footer__logo-wrapper">
            <span class="sr-only">Cougar Club</span>
            <VLazyImage :src="websiteLogo || WebsiteLogoImage" alt="Cougar Club" class="footer__logo" />
          </Link>
          <p class="footer__copyright">Copyright © {{ new Date().getFullYear() }}, Cougar Club. All rights reserved</p>
        </div>
        <nav class="footer__menu">
          <ul class="footer__menu-list">
            <template v-for="menuItem in filteredMenuItems">
              <Component
                :is="menuItem.component"
                v-if="menuItem.component !== null"
                :key="menuItem.id"
                :item="menuItem"
              />
            </template>
          </ul>
        </nav>
      </div>

      <div class="footer__bottom">
        <div class="footer__bottom-links">
          <a href="#" class="footer__bottom-link">Privacy Notice</a>
          <a href="#" class="footer__bottom-link">Cookie Preferences</a>
        </div>
        <a href="https://wmt.digital/" target="_blank" rel="noopener" class="footer__powered">
          <span class="footer__powered-label">Powered by</span>
          <VLazyImage :src="wmtLogo" alt="WMT Digital" class="footer__powered-image" />
          <ExternalLinkWarning />
        </a>
      </div>
    </Container>
  </footer>
</template>

<style lang="scss" scoped>
.footer {
  @apply rounded-t-[20px] z-[2] bg-white py-10 -mt-8 relative;
  @apply xl:rounded-t-30;

  &__container {
    @apply flex flex-col items-center gap-8;
  }

  &__top {
    @apply flex flex-col items-center gap-8 w-full;
    @apply xl:flex-row xl:justify-between;
  }

  &__copyright-box {
    @apply flex flex-col items-center gap-4 text-center;
    @apply xl:flex-row xl:text-start;
  }

  &__bottom {
    @apply flex flex-col gap-6 items-center w-full;
    @apply xl:flex-row xl:justify-between;
  }

  &__powered {
    @apply flex items-center gap-2 [&>span]:hover:text-accent;

    &-image {
      @apply h-5;
    }

    &-label {
      @apply whitespace-nowrap text-dark/70 text-[14px] duration-300;
    }
  }

  &__bottom-links {
    @apply flex items-center gap-5;
  }

  &__menu {
    &-list {
      @apply flex flex-col gap-6;
      @apply md:flex-wrap md:flex-row md:gap-10 md:gap-x-16;
    }
  }

  &__logo {
    @apply h-[46px];
  }

  :deep(.social__name) {
    @apply block;
  }

  :deep(.social__icon) {
    @apply hidden;
  }

  :deep(.menu-item__link) {
    @apply text-accent hover:underline;
  }
}
</style>
